import { getCountryById } from './countrySettings';
import { normalizeCategoryId } from './categories';

const UK_ZERO_RATED_CATEGORY_IDS = new Set([
  'medical_expenses',
  'insurance',
  'rent'
]);

const UK_REDUCED_RATED_CATEGORY_IDS = new Set([
  'utilities'
]);

const PRODUCT_CATEGORY_HINTS = new Set([
  'retail',
  'retail_stock',
  'products',
  'productsales',
  'assetsale'
]);

const EURO_SYMBOL_VARIANTS = new Set([
  '€',
  'EUR',
  'â‚¬',
  'Ã¢â€šÂ¬',
  '\u20AC',
  'ƒ,ª',
  'Æ’,Âª',
  'ÃƒÂ¢Ã¢â€šÂ¬Ã…Â¡'
]);

const POUND_SYMBOL_VARIANTS = new Set([
  '£',
  'GBP',
  'Â£'
]);

const DOLLAR_SYMBOL_VARIANTS = new Set([
  '$',
  'USD'
]);

const sanitizeCountryId = (value) => String(value || '').trim().toLowerCase();

const resolveCountryId = (countryInput) => {
  if (typeof countryInput === 'string' && countryInput.trim()) {
    return sanitizeCountryId(countryInput);
  }

  if (countryInput && typeof countryInput === 'object') {
    if (countryInput.id) return sanitizeCountryId(countryInput.id);
    if (countryInput.countryId) return sanitizeCountryId(countryInput.countryId);
  }

  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('selectedCountry');
    if (stored) return sanitizeCountryId(stored);
  }

  return 'ie';
};

export const getSelectedCountry = (countryInput) => {
  const countryId = resolveCountryId(countryInput);
  return getCountryById(countryId);
};

export const getCountryCurrencySymbol = (countryInput) => {
  const country = getSelectedCountry(countryInput);
  if (country?.currency === 'GBP') return '£';
  if (country?.currency === 'USD') return '$';
  return '€';
};

export const getCountryVatRateProfile = (countryInput) => {
  const country = getSelectedCountry(countryInput);
  const decimalRates = Array.isArray(country?.vatRates)
    ? country.vatRates
        .map((entry) => Number(entry?.rate) / 100)
        .filter((rate) => Number.isFinite(rate) && rate >= 0)
    : [];

  const standardRate = Number.isFinite(Number(country?.defaultVatRate))
    ? Number(country.defaultVatRate) / 100
    : (decimalRates.length ? Math.max(...decimalRates) : 0);

  const reducedRates = decimalRates.filter((rate) => rate > 0 && rate < standardRate).sort((a, b) => b - a);
  const reducedRate = reducedRates.length ? reducedRates[0] : standardRate;
  const zeroRate = decimalRates.find((rate) => Math.abs(rate) < 0.0001) ?? 0;

  return {
    country,
    standardRate,
    reducedRate,
    zeroRate,
    rates: decimalRates.length ? decimalRates : [standardRate, reducedRate, zeroRate]
  };
};

export const getCountryVatRateForCategory = (category, countryInput) => {
  const { country, standardRate, reducedRate, zeroRate } = getCountryVatRateProfile(countryInput);
  const normalizedCategory = normalizeCategoryId(category).toLowerCase();

  if (country?.taxSystem === 'sales' || country?.id === 'us') {
    return 0;
  }

  if (country?.id === 'uk') {
    if (UK_ZERO_RATED_CATEGORY_IDS.has(normalizedCategory)) return zeroRate;
    if (UK_REDUCED_RATED_CATEGORY_IDS.has(normalizedCategory)) return reducedRate;
    return standardRate;
  }

  if (PRODUCT_CATEGORY_HINTS.has(normalizedCategory)) {
    return standardRate;
  }

  return reducedRate;
};

const looksLikeGarbledCurrency = (value) => /Ã|â|Æ|ƒ/.test(value);

export const normalizeCurrencySymbol = (value, countryInput) => {
  const defaultSymbol = getCountryCurrencySymbol(countryInput);
  const normalized = String(value || '').trim();

  if (!normalized) return defaultSymbol;

  if (EURO_SYMBOL_VARIANTS.has(normalized)) {
    return defaultSymbol === '£' || defaultSymbol === '$' ? defaultSymbol : '€';
  }
  if (POUND_SYMBOL_VARIANTS.has(normalized)) {
    return '£';
  }
  if (DOLLAR_SYMBOL_VARIANTS.has(normalized)) {
    return '$';
  }

  if (looksLikeGarbledCurrency(normalized)) return defaultSymbol;
  if (normalized.length > 3) return defaultSymbol;

  return normalized;
};

export const normalizeVatRateToCountry = (rate, countryInput, tolerance = 0.04) => {
  if (rate === null || rate === undefined) return null;
  if (typeof rate === 'string' && rate.trim() === '') return null;

  const numericRate = Number(rate);
  if (!Number.isFinite(numericRate) || numericRate < 0) return null;

  const { country, rates } = getCountryVatRateProfile(countryInput);

  if (country?.id === 'uk') {
    if (Math.abs(numericRate - 0.23) <= 0.05) return 0.2;
    if (Math.abs(numericRate - 0.135) <= 0.05) return 0.05;
    if (Math.abs(numericRate - 0.09) <= 0.05) return 0;
  }

  const effectiveTolerance = Number.isFinite(Number(tolerance)) ? Number(tolerance) : 0.04;
  const countryTolerance = country?.id === 'uk'
    ? Math.max(effectiveTolerance, 0.12)
    : effectiveTolerance;
  const uniqueRates = Array.from(new Set(rates.filter((entry) => Number.isFinite(entry))));

  if (!uniqueRates.length || country?.taxSystem === 'sales') {
    return numericRate;
  }

  const nearest = uniqueRates.reduce((best, candidate) => {
    if (best === null) return candidate;
    const bestDelta = Math.abs(best - numericRate);
    const candidateDelta = Math.abs(candidate - numericRate);
    return candidateDelta < bestDelta ? candidate : best;
  }, null);

  if (nearest === null) return numericRate;
  return Math.abs(nearest - numericRate) <= countryTolerance ? nearest : numericRate;
};

export const extractVatNumberFromText = (text, countryInput) => {
  if (!text) return null;
  const country = getSelectedCountry(countryInput);

  if (country?.id === 'uk') {
    const ukMatch = String(text).match(/\bGB\s?\d{9}(?:\s?\d{3})?\b/i);
    if (ukMatch) return ukMatch[0].replace(/\s+/g, ' ').trim().toUpperCase();
  }

  const ieMatch = String(text).match(/\bIE\s?\d{7}[A-Z]{1,2}\b/i);
  if (ieMatch) return ieMatch[0].replace(/\s+/g, ' ').trim().toUpperCase();

  const genericMatch = String(text).match(/\b(?:VAT|VAT\s*No\.?|VAT\s*Number)\s*[:#-]?\s*([A-Z]{0,2}\s?\d{8,12}[A-Z]{0,2})\b/i);
  if (genericMatch?.[1]) {
    return genericMatch[1].replace(/\s+/g, ' ').trim().toUpperCase();
  }

  return null;
};

const calculateVatFromGross = (grossAmount, vatRate) => {
  const gross = Number(grossAmount);
  const rate = Number(vatRate);
  if (!Number.isFinite(gross) || gross <= 0 || !Number.isFinite(rate) || rate <= 0) return 0;
  return gross - (gross / (1 + rate));
};

const formatRatePercent = (rate) => {
  const percent = Number(rate) * 100;
  if (!Number.isFinite(percent)) return '0';
  const rounded = Math.round(percent * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : `${rounded.toFixed(1)}`;
};

export const normalizeVatDataForCountry = (extractedData, options = {}) => {
  if (!extractedData || typeof extractedData !== 'object') return extractedData;

  const countryInput = options.country || options.countryId;
  const responseText = options.text || '';
  const normalizedData = { ...extractedData };
  const looksLikeExplicitInvoiceVat =
    /\b(sales\s+invoice|invoice\s+no|vat\s*code|vat\s*rate|total\s+invoice\s+value|net\s+goods)\b/i.test(responseText);

  normalizedData.currency = normalizeCurrencySymbol(normalizedData.currency, countryInput);

  const originalTopRate = Number(normalizedData.vatRate);
  const normalizedTopRate = normalizeVatRateToCountry(normalizedData.vatRate, countryInput);
  normalizedData.vatRate = normalizedTopRate;

  const normalizedAmount = Number(normalizedData.amount);
  const normalizedVatAmount = Number(normalizedData.vatAmount);
  const shouldRecalculateTopVatAmount =
    Number.isFinite(normalizedTopRate) &&
    normalizedTopRate > 0 &&
    Number.isFinite(normalizedAmount) &&
    normalizedAmount > 0 &&
    (
      normalizedData.vatAmount === null ||
      !Number.isFinite(normalizedVatAmount) ||
      (
        !looksLikeExplicitInvoiceVat &&
        Number.isFinite(originalTopRate) &&
        Math.abs(originalTopRate - normalizedTopRate) > 0.0001
      )
    );
  if (shouldRecalculateTopVatAmount) {
    normalizedData.vatAmount = parseFloat(calculateVatFromGross(normalizedAmount, normalizedTopRate).toFixed(2));
  }

  normalizedData.items = Array.isArray(normalizedData.items)
    ? normalizedData.items.map((item) => {
        const nextItem = { ...item };
        const originalItemRate = Number(nextItem.vatRate);
        const itemRate = normalizeVatRateToCountry(nextItem.vatRate, countryInput);
        if (itemRate !== null) nextItem.vatRate = itemRate;

        const itemAmount = Number(nextItem.amount);
        const itemVatAmount = Number(nextItem.vatAmount);
        const shouldRecalculateItemVatAmount =
          Number.isFinite(itemRate) &&
          itemRate > 0 &&
          Number.isFinite(itemAmount) &&
          itemAmount > 0 &&
          (
            nextItem.vatAmount === null ||
            !Number.isFinite(itemVatAmount) ||
            (Number.isFinite(originalItemRate) && Math.abs(originalItemRate - itemRate) > 0.0001)
          );
        if (shouldRecalculateItemVatAmount) {
          nextItem.vatAmount = parseFloat(calculateVatFromGross(itemAmount, itemRate).toFixed(2));
        }

        return nextItem;
      })
    : [];

  normalizedData.vatComponents = Array.isArray(normalizedData.vatComponents)
    ? normalizedData.vatComponents.map((component) => {
        const nextComponent = { ...component };
        const componentRate = normalizeVatRateToCountry(nextComponent.rate, countryInput);
        if (componentRate !== null) nextComponent.rate = componentRate;

        if (typeof nextComponent.description === 'string' && Number.isFinite(componentRate)) {
          if (/\d+(?:[.,]\d+)?\s*%/.test(nextComponent.description)) {
            nextComponent.description = nextComponent.description.replace(/\d+(?:[.,]\d+)?\s*%/g, `${formatRatePercent(componentRate)}%`);
          } else if (!nextComponent.description.trim()) {
            nextComponent.description = `${formatRatePercent(componentRate)}% VAT`;
          }
        } else if (!nextComponent.description && Number.isFinite(componentRate)) {
          nextComponent.description = `${formatRatePercent(componentRate)}% VAT`;
        }
        return nextComponent;
      })
    : [];

  if (!normalizedData.vatNumber) {
    const extractedVatNumber = extractVatNumberFromText(responseText, countryInput);
    if (extractedVatNumber) {
      normalizedData.vatNumber = extractedVatNumber;
    }
  }

  return normalizedData;
};

export const getUkVatRegistrationThreshold = () => 90000;
